import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import classes from "./AddDepartmentModal.module.css";
import { FiX, FiPlus, FiCheckCircle, FiChevronDown, FiCheck } from "react-icons/fi";
import { BASE_URL } from "../../Components/API/Api";

// --- CUSTOM SEARCHABLE SELECT COMPONENT (Built-in) ---
const SearchableSelect = ({ options, placeholder, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = options.filter((opt) =>
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={classes.searchWrapper} ref={wrapperRef}>
      <div className={classes.searchInputArea} onClick={() => setIsOpen(!isOpen)}>
        <input
          type="text"
          placeholder={placeholder}
          value={value ? value : searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          className={classes.searchInput}
        />
        <FiChevronDown
          className={`${classes.searchChevron} ${isOpen ? classes.searchChevronOpen : ""}`}
        />
      </div>

      {isOpen && (
        <div className={classes.searchDropdown}>
          {filtered.length > 0 ? (
            filtered.map((opt) => (
              <div
                key={opt}
                className={classes.searchOption}
                onClick={() => {
                  onChange(opt);
                  setSearchTerm(opt);
                  setIsOpen(false);
                }}
              >
                <span>{opt}</span>
                {value === opt && <FiCheck className={classes.checkIcon} />}
              </div>
            ))
          ) : (
            <div className={classes.noOptions}>No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

// --- MAIN MODAL ---
const AddDepartmentModal = ({ isOpen, onClose, onDepartmentAdded }) => {
  const [formData, setFormData] = useState({
    departmentName: "",
    departmentType: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Department Types
  const departmentTypes = [
    "Clinical / Medical",
    "Surgical",
    "Emergency & Acute Care",
    "Maternal & Women's Health",
    "Paediatric & Neonatal",
    "Diagnostic Services",
    "Oncology / Cancer Care",
    "Mental & Behavioural Health",
    "Rehabilitation",
    "Pharmacy",
    "Dental & Oral Health",
    "Outpatient Services",
    "Inpatient Services",
    "Nursing Services",
    "Public / Community Health",
    "Infection Prevention & Control",
    "Medical Records & Health Information",
    "Administration",
    "Support Services",
    "Information Technology / Digital Health",
    "Research & Education",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
    setSuccessMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    setSuccessMessage("");

    // Validate fields
    const newErrors = {};
    if (!formData.departmentName || formData.departmentName.trim() === "") {
      newErrors.departmentName = "Department Name is required.";
    }
    if (!formData.departmentType || formData.departmentType.trim() === "") {
      newErrors.departmentType = "Department Type is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      // Get company_id from localStorage
      const companyId = localStorage.getItem("companyId") || "1";
      
      // Prepare payload - exactly what backend expects with company_id
      const payload = {
        company_id: parseInt(companyId),
        name: formData.departmentName.trim(),
        type: formData.departmentType.trim(),
      };

      console.log("Sending to API:", payload);

      // API call to create department
      const response = await axios.post(
        `${BASE_URL}/api/department/create`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      console.log("API Response:", response.data);

      if (response.data) {
        setSuccessMessage("Department created successfully!");
        
        // Reset form
        setFormData({
          departmentName: "",
          departmentType: "",
        });

        // Call the callback if provided
        if (onDepartmentAdded) {
          onDepartmentAdded(response.data.data || response.data);
        }

        // Close modal after delay
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error("API Error:", error);
      
      let errorMsg = "Failed to create department. Please try again.";
      
      if (error.response) {
        const apiErrors = error.response.data?.data;
        if (apiErrors && typeof apiErrors === "object") {
          const firstKey = Object.keys(apiErrors)[0];
          if (firstKey && Array.isArray(apiErrors[firstKey])) {
            errorMsg = apiErrors[firstKey][0];
          }
        } else if (error.response.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.response.status === 401) {
          errorMsg = "Unauthorized. Please login again.";
        } else if (error.response.status === 422) {
          errorMsg = "Validation failed. Please check your input.";
        }
      } else if (error.request) {
        errorMsg = "Unable to connect to server. Please check your internet connection.";
      } else {
        errorMsg = error.message || "An unexpected error occurred.";
      }

      setErrors({ submit: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setErrors({});
      setSuccessMessage("");
      setFormData({
        departmentName: "",
        departmentType: "",
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalContainer}>
        {/* Header */}
        <div className={classes.modalHeader}>
          <div className={classes.headerTitle}>
            <div className={classes.modalIcon}>
              <FiPlus />
            </div>
            <div>
              <h2 className={classes.modalTitle}>ADD NEW DEPARTMENT</h2>
              <p className={classes.modalSubtitle}>Fill in the department details below</p>
            </div>
          </div>
          <button 
            className={classes.closeBtn} 
            onClick={handleClose}
            disabled={isLoading}
          >
            <FiX />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className={classes.modalBody}>
            {/* Department Information */}
            <div className={classes.section}>
              <h3 className={classes.sectionTitle}>Department Information</h3>

              <div className={classes.row2Col}>
                <div className={classes.inputGroup}>
                  <label>
                    Department Name <span className={classes.required}>*</span>
                  </label>
                  <input
                    type="text"
                    name="departmentName"
                    placeholder="Enter department name (e.g., Cardiology)"
                    className={`${classes.formInput} ${errors.departmentName ? classes.inputError : ''}`}
                    value={formData.departmentName}
                    onChange={handleChange}
                    disabled={isLoading}
                    maxLength={100}
                  />
                  {errors.departmentName && (
                    <span className={classes.errorText}>{errors.departmentName}</span>
                  )}
                </div>

                <div className={classes.inputGroup}>
                  <label>
                    Department Type <span className={classes.required}>*</span>
                  </label>
                  <SearchableSelect
                    options={departmentTypes}
                    placeholder="Search and select department type..."
                    value={formData.departmentType}
                    onChange={(val) => {
                      setFormData((prev) => ({ ...prev, departmentType: val }));
                      if (errors.departmentType) {
                        setErrors((prev) => ({ ...prev, departmentType: null }));
                      }
                    }}
                  />
                  {errors.departmentType && (
                    <span className={classes.errorText}>{errors.departmentType}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className={classes.successMessage}>
                <FiCheckCircle className={classes.successIcon} />
                <span>{successMessage}</span>
              </div>
            )}

            {/* Submit Error */}
            {errors.submit && (
              <div className={classes.submitError}>
                <span>{errors.submit}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={classes.modalFooter}>
            <button
              type="button"
              className={classes.cancelBtn}
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={classes.saveBtn}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className={classes.spinner}></span>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FiCheckCircle /> Save Department
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDepartmentModal;